import {getTestProps} from '@benchling/js-test-utils';
import {
  Alert,
  Anchor,
  Button,
  Group,
  Menu,
  Progress,
  Stack,
  Text,
  UnstyledButton,
  createStyles,
} from '@mantine/core';
import React from 'react';

import {redirectToSignin, setURLParams} from 'BenchlingUtil';
import {User} from 'constants/UserConstants';
import navigateToURL from 'lib/navigateToURL';
import {
  derivePredictionUsage,
  isPredictionUsageNearlyExhausted,
  type PredictionUsageState,
} from 'model-hub/lib/predictionUsage';
import {useCurrentUserAiCreditUsage} from 'model-hub/lib/useCurrentUserAiCreditUsage';
import {accountPath} from 'model-hub/modelHubRoutes';
import {redirectToModelHubSignup} from 'model-hub/modelHubSignupRedirect';
import {PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL} from 'model-hub/signup/constants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import EntityStore from 'stores/EntityStore';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {submitCSRFForm} from 'util/Net';

function handleSignOut(): void {
  submitCSRFForm('POST', setURLParams('/signout', {next: '/signin'}));
}

function SignedOutActions(): JSX.Element {
  const {classes} = useStyles();
  return (
    <div
      className={classes.root}
      {...getTestProps({component: 'ModelHubProfileSection', element: 'signed-out'})}
    >
      <Stack spacing="xs">
        <Button fullWidth={true} variant="primary" onClick={redirectToModelHubSignup}>
          Create account
        </Button>
        <Button
          fullWidth={true}
          variant="secondary"
          onClick={() => {
            redirectToSignin();
          }}
        >
          Sign in
        </Button>
      </Stack>
      <Group position="center" spacing="xs">
        <Anchor
          color="dimmed"
          href={TERMS_OF_SERVICE_URL}
          rel="noopener noreferrer"
          size="xs"
          target="_blank"
        >
          Terms of Service
        </Anchor>
        <Text color="dimmed" size="xs">
          ·
        </Text>
        <Anchor color="dimmed" href={PRIVACY_POLICY_URL} rel="noopener noreferrer" size="xs" target="_blank">
          Privacy Policy
        </Anchor>
      </Group>
    </div>
  );
}

// Takes the usage state as a prop rather than reading it directly: the contact-us link's visibility
// depends on the same state, so the parent owns it and both stay consistent.
function CreditsUsage({usageState}: {usageState: PredictionUsageState}): JSX.Element | null {
  const {classes} = useStyles();

  if (usageState.status === 'loading') {
    return null;
  }

  if (usageState.status === 'unavailable') {
    return (
      <Alert role="alert" variant="warning">
        No predictions granted — contact Benchling for help
      </Alert>
    );
  }

  const {predictionsGranted, predictionsUsed, usagePercentage} = usageState.usage;

  return (
    <div
      className={classes.credits}
      {...getTestProps({component: 'ModelHubProfileSection', element: 'credits'})}
    >
      <div className={classes.creditsRow}>
        <Text className={classes.creditsLabel}>Predictions used this month</Text>
        <Text className={classes.creditsValue}>
          {predictionsUsed} of {predictionsGranted}
        </Text>
      </div>
      <Progress
        aria-label="Predictions used this month"
        color="blue"
        radius="xl"
        size="sm"
        value={usagePercentage}
      />
    </div>
  );
}

interface ModelHubProfileSectionProps {
  onContactUs: () => void;
}

export default function ModelHubProfileSection({
  onContactUs,
}: ModelHubProfileSectionProps): JSX.Element | null {
  const {classes} = useStyles();
  const currentUser = EntityStore.currentUser() as User | null;
  // Read before the signed-out early return, since hooks can't sit after a conditional return. The
  // signed-out branch just ignores it.
  const usageState = derivePredictionUsage(useCurrentUserAiCreditUsage());

  // Signed-out users can't reach the feedback endpoint, so the early return also hides the link.
  if (currentUser == null) {
    return <SignedOutActions />;
  }

  // Use `||` (not `??`) so an empty display string (getPartyDisplayString returns
  // `name || handle`, which can be '') also falls back to the email.
  const displayName = getPartyDisplayString(currentUser) || currentUser.email;

  return (
    <div className={classes.root} {...getTestProps({component: 'ModelHubProfileSection', element: 'root'})}>
      <Stack spacing={6}>
        <CreditsUsage usageState={usageState} />
        {/* Only offered once predictions are nearly spent — that's when asking for more is the
            user's next step, and a permanent link here would just be noise. The page-header "Get in
            touch" button remains available for issue reports at any usage level. */}
        {isPredictionUsageNearlyExhausted(usageState) && (
          <UnstyledButton
            className={classes.contactUsButton}
            onClick={onContactUs}
            {...getTestProps({component: 'ModelHubProfileSection', element: 'contact-us'})}
          >
            Contact us
          </UnstyledButton>
        )}
      </Stack>

      <Menu position="right-end" shadow="md" width={MENU_WIDTH_PX} withinPortal={true}>
        <Menu.Target>
          <UnstyledButton
            aria-label="Open profile menu"
            className={classes.profileButton}
            {...getTestProps({component: 'ModelHubProfileSection', element: 'trigger'})}
          >
            <div className={classes.profileText}>
              <Text className={classes.profileName} truncate={true}>
                {displayName}
              </Text>
              <Text className={classes.profileEmail} truncate={true}>
                {currentUser.email}
              </Text>
            </div>
            <BenchlingIcon className={classes.profileChevron} icon="chevron-right" />
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            icon={<BenchlingIcon icon="settings" />}
            onClick={() => {
              navigateToURL(accountPath());
            }}
          >
            Settings
          </Menu.Item>
          <Menu.Item icon={<BenchlingIcon icon="sign-out" />} onClick={handleSignOut}>
            Log out
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </div>
  );
}

const MENU_WIDTH_PX = 200;

const useStyles = createStyles((theme) => ({
  root: {
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
    marginTop: theme.spacing.md,
    paddingTop: 14,
  },
  credits: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '0 6px',
  },
  creditsRow: {
    alignItems: 'baseline',
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  creditsLabel: {
    color: theme.colors.gray[6],
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.2,
  },
  creditsValue: {
    color: theme.colors.gray[8],
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  },
  contactUsButton: {
    borderRadius: 6,
    color: theme.colors.blue[8],
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 6px',
    textAlign: 'center' as const,
    width: '100%',

    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  profileButton: {
    alignItems: 'center',
    borderRadius: 8,
    display: 'flex',
    gap: 10,
    padding: '8px 10px',
    width: '100%',

    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  profileText: {
    flex: 1,
    minWidth: 0,
  },
  profileName: {
    color: theme.colors.gray[9],
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  profileEmail: {
    color: theme.colors.gray[6],
    fontSize: 12.5,
    lineHeight: 1.3,
  },
  profileChevron: {
    color: theme.colors.gray[6],
    fontSize: 14,
    flexShrink: 0,
  },
}));
